import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Modal, Row, Button } from 'antd';
import cx from 'classnames';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';

import {
  AssignmentTerminationModel,
  RenewAssignmentModel,
  RenewKindEnum,
} from 'api/backofficeApiClient';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { useAppDispatch } from 'hooks/useAppDispatch';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import {
  getAssignmentTerminationModel,
  rehireAssignmentWithoutChanges,
} from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import SubmitButton from 'components/formik/SubmitButton';
import {
  disableDatesBeforeSpecificDate,
  disablePastDates,
  getDateWithoutTime,
} from 'utils/dateUtils';
import { validateNoEndDateLessStartDate } from 'utils/validationUtils';
import RehireTypeSelectionModal from '../RehireTypeSelectionModal';
import { BreakInServiceEnum } from 'api/adminApiClient';
import { isTerminationDateInFuture } from 'utils/assignmentUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';

import styles from './RehireTypeSelectionModal.module.less';

const getValidationSchema = (hasRehireDateRestriction: boolean) =>
  Yup.object().shape(
    {
      startDate: Yup.date()
        .nullable()
        .required('Field is required')
        .when('endDate', {
          is: () => hasRehireDateRestriction,
          then: Yup.date().min(
            new Date(new Date().setHours(0, 0, 0, 0)),
            'Start date should not be in the past',
          ),
        }),
      endDate: Yup.date()
        .nullable()
        .required('Field is required')
        .when('startDate', {
          is: (startDate?: Date | null) => !!startDate,
          then: Yup.date()
            .nullable()
            .test(
              'isEndDateLessThanStartDate',
              'End date cannot be less than start date',
              function (endDate?: Date | null) {
                if (!endDate) return true;

                return validateNoEndDateLessStartDate(
                  this.parent.startDate,
                  endDate,
                );
              },
            ),
        }),
    },
    [['endDate', 'startDate']],
  );

interface AssignmentRehireFormValues {
  startDate: string | null;
  endDate: string | null;
}

type Props = {
  assignmentId: string;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  breakInServiceEnumValue?: BreakInServiceEnum;
  companyId?: string;
};

enum RehireTypeEnum {
  WithChanges = 1,
  WithoutChanges = 2,
}

export default function RehireActionSelectionModal(props: Props) {
  const {
    onClose,
    onSubmit,
    assignmentId,
    breakInServiceEnumValue,
    companyId,
  } = props;
  const formRef: any = useRef();
  const dispatch = useAppDispatch();
  const [activeRehireType, setActiveRehireType] = useState<RehireTypeEnum>();
  const [isRehireSaving, setIsRehireSaving] = useState(false);
  const [dateFieldsEnabled, setDateFieldsEnabled] = useState(false);
  const [isRehireTypeSelectionModalOpen, setIsRehireTypeSelectionModalOpen] =
    useState(false);
  const [assignmentTerminationDetails, setAssignmentTerminationDetails] =
    useState<AssignmentTerminationModel>();
  const isAdminRole = useAppSelector(isAdminSelector);
  const hasActiveScheduledTerminationInFuture = isTerminationDateInFuture(
    assignmentTerminationDetails?.terminationDate,
  );
  const hasRehireDateRestriction =
    hasActiveScheduledTerminationInFuture &&
    breakInServiceEnumValue === BreakInServiceEnum.Days0;

  const initialValues: AssignmentRehireFormValues = {
    startDate: null,
    endDate: null,
  };

  const loadAssignmentTermination = useCallback(async () => {
    if (!assignmentId) return;
    const result = await dispatch(
      getAssignmentTerminationModel({
        assignmentId: assignmentId,
        isAdmin: isAdminRole,
      }),
    );

    if (getAssignmentTerminationModel.fulfilled.match(result)) {
      setAssignmentTerminationDetails(result.payload);
    } else {
      setAssignmentTerminationDetails(undefined);
    }
  }, [dispatch, isAdminRole, assignmentId]);

  useEffect(() => {
    loadAssignmentTermination();
  }, [loadAssignmentTermination]);

  const handleRehireTypeSelectionModalOpen = () => {
    setIsRehireTypeSelectionModalOpen(true);
  };

  const handleRehireTypeSelectionModalClose = () => {
    setIsRehireTypeSelectionModalOpen(false);
    onClose();
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const displayDateFields = (
    formikHelpers: FormikHelpers<AssignmentRehireFormValues>,
  ) => {
    formikHelpers.setFieldTouched('startDate', false, false);
    formikHelpers.setFieldTouched('endDate', false, false);

    setDateFieldsEnabled(true);
  };

  const handleSubmit = async (
    values: AssignmentRehireFormValues,
    formikHelpers: FormikHelpers<AssignmentRehireFormValues>,
  ) => {
    if (activeRehireType === RehireTypeEnum.WithChanges)
      return handleRehireTypeSelectionModalOpen();

    if (!dateFieldsEnabled) {
      return displayDateFields(formikHelpers);
    }

    const { startDate, endDate } = values;

    if (!startDate || !endDate) return;

    const data: RenewAssignmentModel = {
      jobStartDate: getDateWithoutTime(startDate),
      jobEndDate: getDateWithoutTime(endDate),
      parentAssignmentId: assignmentId,
      renewKind: RenewKindEnum.Rehire,
    };

    setIsRehireSaving(true);
    const result = await dispatch(
      rehireAssignmentWithoutChanges({ data, isAdminRole }),
    );
    setIsRehireSaving(false);

    if (rehireAssignmentWithoutChanges.fulfilled.match(result)) {
      BaseAlert.success(
        'Rehire request submitted. Upon approval your changes will become visible here.',
      );
      onClose();

      await onSubmit();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <>
      {isRehireTypeSelectionModalOpen && (
        <RehireTypeSelectionModal
          onSubmit={onSubmit}
          onClose={handleRehireTypeSelectionModalClose}
          assignmentId={assignmentId}
          hasRehireDateRestriction={hasRehireDateRestriction}
          companyId={companyId}
        />
      )}

      {!isRehireTypeSelectionModalOpen && (
        <BaseModal
          title="Select rehire type"
          open={true}
          onCancel={handleCloseModal}
          destroyOnClose
          maxWidth={570}
        >
          <Formik
            innerRef={formRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={
              dateFieldsEnabled
                ? getValidationSchema(hasRehireDateRestriction)
                : null
            }
          >
            {(formik) => (
              <Form layout="vertical">
                <div className={cx('modal-scroll-wrapper', styles.rehireModal)}>
                  {dateFieldsEnabled && (
                    <>
                      <div className={styles.rehireText}>
                        Select rehire date
                      </div>

                      <Row gutter={[20, 20]}>
                        <Col xs={24} md={12}>
                          <FormItem label="Start date *" name="startDate">
                            <DatePicker
                              name="startDate"
                              placeholder="Select start date"
                              disabledDate={
                                hasRehireDateRestriction
                                  ? disablePastDates
                                  : undefined
                              }
                              showToday={false}
                              format="MM/DD/YYYY"
                              className="d-flex"
                            />
                          </FormItem>
                        </Col>

                        <Col xs={24} md={12}>
                          <FormItem
                            label="End date (expected) *"
                            name="endDate"
                          >
                            <DatePicker
                              key={formik.values.startDate || 'endDate'}
                              name="endDate"
                              placeholder="Select end date"
                              disabledDate={disableDatesBeforeSpecificDate(
                                formik.values.startDate,
                              )}
                              showToday={false}
                              format="MM/DD/YYYY"
                              className="d-flex"
                            />
                          </FormItem>
                        </Col>
                      </Row>
                    </>
                  )}

                  {!dateFieldsEnabled && (
                    <>
                      <div className={styles.rehireText}>
                        Select the action below
                      </div>

                      <Row gutter={[20, 20]}>
                        <Col xs={24} md={12}>
                          <div
                            className={cx(styles.card, {
                              [styles.isActive]:
                                activeRehireType === RehireTypeEnum.WithChanges,
                            })}
                          >
                            <Button
                              onClick={() =>
                                setActiveRehireType(RehireTypeEnum.WithChanges)
                              }
                            >
                              Rehire with changes
                            </Button>
                          </div>
                        </Col>

                        <Col xs={24} md={12}>
                          <div
                            className={cx(styles.card, {
                              [styles.isActive]:
                                activeRehireType ===
                                RehireTypeEnum.WithoutChanges,
                            })}
                          >
                            <Button
                              onClick={() =>
                                setActiveRehireType(
                                  RehireTypeEnum.WithoutChanges,
                                )
                              }
                            >
                              Rehire without changes{' '}
                              <span className="d-sm-none">(dates only)</span>
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>

                <BaseModalFooter
                  className={cx('button-space-between', styles.rehireFooter)}
                >
                  <Button
                    className="modal-icon-btn d-xs-none"
                    onClick={handleCloseModal}
                  >
                    <span className="icon icon-closed-outlined" />
                    Cancel
                  </Button>

                  <SubmitButton
                    type="primary"
                    disabled={
                      formik.isSubmitting || !activeRehireType || isRehireSaving
                    }
                  >
                    Next
                  </SubmitButton>
                </BaseModalFooter>
              </Form>
            )}
          </Formik>
        </BaseModal>
      )}
    </>
  );
}
